import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const Hero = () => {
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);

  const handleAppStoreClick = () => {
    window.open('https://apps.apple.com/pt/app/am-cardmate/id6747951046', '_blank');
  };

  const handleGooglePlayClick = () => {
    window.open('https://play.google.com/store/apps/details?id=com.amcardmate.app', '_blank');
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-24 sm:pt-32 pb-24 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div 
          className="absolute top-[20%] left-[10%] w-[400px] h-[400px] bg-blue-200/20 rounded-full filter blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-[10%] right-[10%] w-[500px] h-[500px] bg-purple-200/20 rounded-full filter blur-3xl"
          animate={{
            scale: [1.1, 1, 1.1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-8">
          {/* Content */}
          <div className="flex-1 max-w-[600px]">
            <div className="badge badge-blue inline-flex mb-6">
              <FontAwesomeIcon icon="star" />
              <span>Next Generation Digital Cards</span>
            </div>
            
            <h1 className="text-h1 mb-6 text-gray-900">
              Paper cards get lost.<br />
              <span className="text-gradient-blue-pink">
                AM CARDMATE doesn't.
              </span>
            </h1>
            
            <p className="text-body-lg text-gray-600 mb-8 leading-relaxed">
              Create, share, and manage your digital business cards with AM CARDMATE. Connect seamlessly with premium NFC card options and build your professional network effortlessly.
            </p>
            
            <div className="flex flex-wrap gap-5 mb-10">
              <button onClick={() => setShowComingSoonPopup(true)} className="btn btn-primary shadow-lg hover:shadow-xl">
                <FontAwesomeIcon icon="download" />
                <span>Download App</span>
              </button>
              
              <a href="#features" className="btn btn-outline">
                <span>Explore Features</span>
              </a>
            </div>
            
            {/* Feature Pills */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-3 px-5 py-2 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300">
                <FontAwesomeIcon icon="check-circle" className="text-primary-blue" />
                <span className="text-body-sm font-medium text-gray-700">No More Paper Cards</span>
              </div>
              
              <div className="flex items-center gap-3 px-5 py-2 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300">
                <FontAwesomeIcon icon="check-circle" className="text-primary-blue" />
                <span className="text-body-sm font-medium text-gray-700">Premium NFC Cards</span>
              </div>
              
              <div className="flex items-center gap-3 px-5 py-2 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300">
                <FontAwesomeIcon icon="check-circle" className="text-primary-blue" />
                <span className="text-body-sm font-medium text-gray-700">Real-time Updates</span>
              </div>
            </div>
          </div>
          
          {/* Image Section */}
          <div className="flex-1 w-full">
            {/* Mobile View */}
            <div className="lg:hidden">
              <div className="relative z-10 mx-auto w-fit mb-4">
                <img 
                  src="/assets/image1.png" 
                  alt="AM CARDMATE App Interface" 
                  className="w-auto h-[480px] sm:h-[520px] mx-auto drop-shadow-2xl"
                  style={{
                    filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1))',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                />
              </div>
              
              {/* Mobile Feature Pills */}
              <div className="flex flex-wrap justify-center gap-2 max-w-sm mx-auto">
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full shadow-md">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white flex-shrink-0">
                    <FontAwesomeIcon icon="check" className="text-xs" />
                  </div>
                  <span className="text-body-sm font-semibold text-gray-900">Shared</span>
                </div>
                
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full shadow-md">
                  <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white flex-shrink-0">
                    <FontAwesomeIcon icon="star" className="text-xs" />
                  </div>
                  <span className="text-body-sm font-semibold text-gray-900">Connected</span>
                </div>
                
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full shadow-md">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white flex-shrink-0">
                    <FontAwesomeIcon icon="sync" className="text-xs" />
                  </div>
                  <span className="text-body-sm font-semibold text-gray-900">Updated</span>
                </div>
              </div>
            </div>
            
            {/* Desktop View */}
            <div className="hidden lg:block relative min-h-[700px] flex items-center justify-center">
              <div className="relative w-full max-w-[700px] mx-auto">
                <div className="relative z-10 mx-auto w-fit">
                  <img 
                    src="/assets/image1.png" 
                    alt="AM CARDMATE App Interface" 
                    className="w-auto h-[600px] mx-auto drop-shadow-2xl"
                    style={{
                      filter: 'drop-shadow(0 30px 60px rgba(0, 0, 0, 0.12))',
                      maxWidth: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                
                {/* Floating Card - Shared */}
                <motion.div 
                  className="absolute top-[5%] left-[-20%] flex items-center gap-3 bg-white p-5 rounded-2xl shadow-xl z-20 min-w-[240px]"
                  animate={{ 
                    y: [0, -10, 0],
                    x: [0, 5, 0],
                  }}
                  transition={{ 
                    duration: 5, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white flex-shrink-0">
                    <FontAwesomeIcon icon="check" className="text-lg" />
                  </div>
                  <div>
                    <div className="text-body font-bold text-gray-900">Card Shared</div>
                    <p className="text-body-sm text-gray-500">Sarah received your business card</p>
                  </div>
                </motion.div>
                
                {/* Floating Card - Connection */}
                <motion.div 
                  className="absolute top-[50%] right-[-15%] flex items-center gap-3 bg-white p-5 rounded-2xl shadow-xl z-20 min-w-[240px]"
                  animate={{ 
                    y: [0, 10, 0],
                    x: [0, -10, 0],
                  }}
                  transition={{ 
                    duration: 6, 
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2
                  }}
                >
                  <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center text-white flex-shrink-0">
                    <FontAwesomeIcon icon="star" className="text-lg" />
                  </div>
                  <div>
                    <div className="text-body font-bold text-gray-900">New Connection</div>
                    <p className="text-body-sm text-gray-500">Michael added your contact</p>
                  </div>
                </motion.div>
                
                {/* Floating Card - Updates */}
                <motion.div 
                  className="absolute bottom-[10%] left-[-15%] flex items-center gap-3 bg-white p-5 rounded-2xl shadow-xl z-20 min-w-[240px]"
                  animate={{ 
                    scale: [1, 1.05, 1],
                  }}
                  transition={{ 
                    duration: 7, 
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 4
                  }}
                >
                  <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white flex-shrink-0">
                    <FontAwesomeIcon icon="sync" className="text-lg" />
                  </div>
                  <div>
                    <div className="text-body font-bold text-gray-900">Updates Applied</div>
                    <p className="text-body-sm text-gray-500">Your card changes are live</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download Popup */}
      {showComingSoonPopup && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowComingSoonPopup(false)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon="rocket" className="text-white text-2xl sm:text-3xl" />
              </div>
              
              <h3 className="text-h4 text-gray-900 mb-4">Download AM CARDMATE</h3>
              <p className="text-body text-gray-600 mb-8">
                AM CARDMATE is available on both App Store and Google Play!
              </p>
              
              <div className="grid grid-cols-1 gap-3 mb-6">
                <button 
                  onClick={handleAppStoreClick}
                  className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all"
                >
                  <FontAwesomeIcon icon={['fab', 'apple']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">Download on the</div>
                    <div className="text-body font-bold">App Store</div>
                  </div>
                </button>
                
                <button 
                  onClick={handleGooglePlayClick}
                  className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all"
                >
                  <FontAwesomeIcon icon={['fab', 'google-play']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">GET IT ON</div>
                    <div className="text-body font-bold">Google Play</div>
                  </div>
                </button>
              </div>
              
              <div className="space-y-3">
                <button className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-btn font-semibold hover:from-blue-600 hover:to-purple-700 transition-all" onClick={() => setShowComingSoonPopup(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;